import {
  makeEnumWithData,
} from '../Base.js'
import { prettyJson, valOr, formatNum, } from '../SharedUtils.js'

export const DEFAULT_PRECISION = 2;
// Enthalpy of dry air at 32F (0C) in the imperial system (0.24 Btu/lb-F * 32F = 7.68 Btu/lb)
// expressed in J/kg.
const ENTHALPY_ZERO_OFFSET_J_PER_KG = 0.24 * 32 * 2326.0;

export let Units = makeEnumWithData({
  None: {
    name: 'None',
    label: '',
  },
  inches: {
    name: 'inches',
    label: 'in',
    conversions: {
      ft: 1.0 / 12.0,
      cm: 2.54,
    },
    metric: 'cm',
    defaultPrecision: 2,
  },
  in2: {
    name: 'in2',
    label: 'in²',
    conversions: {
      ft2: 1.0 / (12.0 * 12.0),
      cm2: 2.54*2.54,
    },
    metric: 'cm2',
    defaultPrecision: 2,
  },
  in3: {
    name: 'in3',
    label: 'in³',
    conversions: {
      ft3: 1.0 / (12.0*12.0*12.0),
      cm3: 2.54*2.54*2.54,
    },
    metric: 'cm3',
    defaultPrecision: 2,
  },
  ft: {
    name: 'ft',
    label: 'ft',
    conversions: {
      m: 0.3048,
    },
    metric: 'm',
    defaultPrecision: 2,
  },
  ft2: {
    name: 'ft2',
    label: 'ft²',
    conversions: {
      m2: 0.3048 * 0.3048,
    },
    metric: 'm2',
    defaultPrecision: 2,
  },
  ft3: {
    name: 'ft3',
    label: 'ft³',
    conversions: {
      m3: 0.3048 * 0.3048 * 0.3048,
    },
    metric: 'm3',
    defaultPrecision: 2,
  },
  cm: {
    name: 'cm',
    label: 'cm',
    conversions: {
      m: 1.0 / 100.0,
    },
    defaultPrecision: 2,
  },
  cm2: {
    name: 'cm2',
    label: 'cm²',
    conversions: {
      m2: 1.0 / (100*100),
    },
    defaultPrecision: 2,
  },
  cm3: {
    name: 'cm3',
    label: 'cm³',
    conversions: {
      m3: 1.0 / (100.0*100.0*100.0),
    },
    defaultPrecision: 2,
  },
  m: {
    name: 'm',
    label: 'm',
    defaultPrecision: 2,
  },
  m2: {
    name: 'm2',
    label: 'm²',
    defaultPrecision: 2,
  },
  m3: {
    name: 'm3',
    label: 'm³',
    defaultPrecision: 2,
  },
  F: {
    name: 'F',
    label: 'F',
    conversions: {
      C: (val) => {
        return (val - 32) * 5/9.0;
      },
    },
    metric: 'C',
    defaultPrecision: 2,
  },
  C: {
    name: 'C',
    label: 'C',
    conversions: {
      F: (val) => {
        return val * 9/5.0 + 32;
      },
    },
    defaultPrecision: 2,
  },
  // Rankine temp
  R: {
    name: 'R',
    label: 'R',
    conversions: {
      K: 5.0/9.0,
    },
    metric: 'K',
    defaultPrecision: 2,
  },
  // Kelvin temp
  K: {
    name: 'K',
    label: 'K',
    defaultPrecision: 2,
  },
  // For temperature differences, we don't
  // want to do offset conversions (just rescale).
  TempDiffF: {
    name: 'TempDiffF',
    label: 'F',
    conversions: {
      TempDiffC: 5.0/9.0,
    },
    metric: 'TempDiffC',
    defaultPrecision: 2,
  },
  TempDiffC: {
    name: 'TempDiffC',
    label: 'C',
    defaultPrecision: 2,
  },
  // Angle degrees
  Degrees: {
    name: 'degrees',
    label: '°',
    defaultPrecision: 2,
  },
  Percent: {
    name: 'percent',
    label: '%',
    defaultPrecision: 2,
  },
  Ratio: {
    name: 'ratio',
    label: '',
    defaultPrecision: 2,
  },
  AirFlow: {
    name: 'cfm',
    label: 'cfm',
    conversions: {
      AirFlowMetric: 1.0/2.11888,
    },
    metric: 'AirFlowMetric',
    defaultPrecision: 2,
  },
  AirFlowMetric: {
    name: 'AirFlowMetric',
    label: 'L/s',
    defaultPrecision: 2,
  },
  AirFlowPerPerson: {
    name: 'cfm/person',
    label: 'cfm/person',
    conversions: {
      AirFlowPerPersonMetric: 1.0/2.11888,
    },
    metric: 'AirFlowPerPersonMetric',
    defaultPrecision: 2,
  },
  AirFlowPerPersonMetric: {
    name: 'AirFlowPerPersonMetric',
    label: 'L/s/person',
    defaultPrecision: 2,
  },
  AirFlowPerArea: {
    name: 'cfm/ft2',
    label: 'cfm/ft²',
    conversions: {
      AirFlowPerAreaMetric: 1.0/2.11888 * (1.0/(0.3048*0.3048)),
    },
    metric: 'AirFlowPerAreaMetric',
    defaultPrecision: 2,
  },
  AirFlowPerAreaMetric: {
    name: 'AirFlowPerAreaMetric',
    label: 'L/s/m²',
    defaultPrecision: 2,
  },
  // Air changes / hr
  ACH: {
    name: 'ACH',
    label: 'ACH',
    defaultPrecision: 2,
  },
  CfmPerFt2WallArea: {
    name: 'cfm/ft2_wallarea',
    label: 'cfm/ft² of wall area',
    conversions: {
      LsPerM2WallArea: 1.0/2.11888 * (1.0/(0.3048*0.3048)),
    },
    metric: 'LsPerM2WallArea',
    defaultPrecision: 2,
  },
  LsPerM2WallArea: {
    name: 'LsPerM2WallArea',
    label: 'L/s/m² of wall area',
    defaultPrecision: 2,
  },
  CfmPerFtCrackLength: {
    name: 'cfm/ft_cracklength',
    label: 'cfm/ft of crack length',
    conversions: {
      LsPerMCrackLength: 1.0/2.11888 * (1.0/0.3048),
    },
    metric: 'LsPerMCrackLength',
    defaultPrecision: 2,
  },
  LsPerMCrackLength: {
    name: 'LsPerMCrackLength',
    label: 'L/s/m of crack length',
    defaultPrecision: 2,
  },
  Load: {
    name: 'Btu/h',
    label: 'Btu/h',
    conversions: {
      LoadMetric: 1.0/3.415179,
    },
    metric: 'LoadMetric',
    defaultPrecision: 2,
  },
  LoadMetric: {
    name: 'LoadMetric',
    label: 'W',
    defaultPrecision: 2,
  },
  LoadMBH: {
    name: 'MBH',
    label: 'MBH',
    conversions: {
      Load: 1000.0,
      LoadMetricKW: 1.0/3.415179,
    },
    metric: 'LoadMetricKW',
    defaultPrecision: 2,
  },
  LoadMetricKW: {
    name: 'kW',
    label: 'kW',
    conversions: {
      LoadMetric: 1000.0,
    },
    defaultPrecision: 2,
  },
  WaterFlow: {
    name: 'gpm',
    label: 'gpm',
    conversions: {
      WaterFlowMetric: 1.0/15.85,
    },
    metric: 'WaterFlowMetric',
    defaultPrecision: 2,
  },
  WaterFlowMetric: {
    name: 'WaterFlowMetric',
    label: 'L/s',
    defaultPrecision: 2,
  },
  FtPerSec: {
    name: 'FtPerSec',
    label: 'ft/s',
    conversions: {
      MPerSec: 0.3048,
    },
    metric: 'MPerSec',
    defaultPrecision: 2,
  },
  MPerSec: {
    name: 'MPerSec',
    label: 'm/s',
    defaultPrecision: 2,
  },
  // Head loss per 100 units of pipe length (ft wc/100 ft or m wc/100 m).
  // These are dimensionless ratios (head/length), so the conversion factor is 1.
  FtWcPer100Ft: {
    name: 'FtWcPer100Ft',
    label: 'ft wc/100 ft',
    conversions: {
      MWcPer100M: 1.0,
    },
    metric: 'MWcPer100M',
    defaultPrecision: 3,
  },
  MWcPer100M: {
    name: 'MWcPer100M',
    label: 'm wc/100 m',
    defaultPrecision: 3,
  },
  PipeDiameterIn: {
    name: 'PipeDiameterIn',
    label: 'in',
    conversions: {
      PipeDiameterMm: 25.4,
    },
    metric: 'PipeDiameterMm',
    defaultPrecision: 3,
  },
  PipeDiameterMm: {
    name: 'PipeDiameterMm',
    label: 'mm',
    defaultPrecision: 1,
  },
  LoadPerArea: {
    name: 'Btu/h/ft2',
    label: 'Btu/h/ft²',
    conversions: {
      WattsPerFt2: 1.0/3.415179,
      WattsPerM2: 1.0/3.415179 * (1.0/(0.3048*0.3048)),
    },
    metric: 'WattsPerM2',
    defaultPrecision: 2,
  },
  WattsPerFt2: {
    name: 'W/ft2',
    label: 'W/ft²',
    conversions: {
      WattsPerM2: 1.0/(0.3048*0.3048),
    },
    metric: 'WattsPerM2',
    defaultPrecision: 2,
  },
  WattsPerM2: {
    name:  'W/m2',
    label: 'W/m²',
    defaultPrecision: 2,
  },
  // Gallons per minute
  GPM: {
    name: 'gpm',
    label: 'GPM',
    conversions: {
      LitersPerMin: 3.78541,
    },
    metric: 'LitersPerMin',
    defaultPrecision: 2,
  },
  LitersPerMin: {
    name: 'L/min',
    label: 'L/min',
    defaultPrecision: 2,
  },
  PoundsPerMin: {
    name: 'lb/min',
    label: 'lb/min',
    conversions: {
      KgPerMin: 0.453592,
    },
    metric: 'KgPerMin',
    defaultPrecision: 2,
  },
  KgPerMin: {
    name: 'kg/min',
    label: 'kg/min',
    defaultPrecision: 2,
  },
  PoundsPerHr: {
    name: 'lb/hr',
    label: 'lb/hr',
    conversions: {
      KgPerHr: 0.453592,
    },
    metric: 'KgPerHr',
    defaultPrecision: 1,
  },
  KgPerHr: {
    name: 'kg/hr',
    label: 'kg/hr',
    defaultPrecision: 1,
  },
  PoundsPerFt2: {
    name: 'lb/ft2',
    label: 'lb/ft²',
    conversions: {
      KgPerM2: 0.453592 * (1.0/(0.3048*0.3048)),
    },
    metric: 'KgPerM2',
    defaultPrecision: 2,
  },
  KgPerM2: {
    name: 'kg/m2',
    label: 'kg/m²',
    defaultPrecision: 2,
  },
  PoundsPerFt3: {
    name: 'lb/ft3',
    label: 'lb/ft³',
    conversions: {
      KgPerM3: 0.453592 / (0.3048 * 0.3048 * 0.3048),
    },
    metric: 'KgPerM3',
    defaultPrecision: 2,
  },
  KgPerM3: {
    name: 'kg/m3',
    label: 'kg/m³',
    defaultPrecision: 2,
  },
  RValue: {
    name: 'RValue',
    label: 'F·ft²·h/Btu',
    conversions: {
      RValueMetric: 1.0/5.678263,
      UValue: (val) => val > 0 ? 1.0 / val : 0,
    },
    metric: 'RValueMetric',
    defaultPrecision: 2,
  },
  RValueMetric: {
    name: 'RValueMetric',
    label: 'K·m²/W',
    conversions: {
      UValueMetric: (val) => val > 0 ? 1.0 / val : 0,
    },
    defaultPrecision: 2,
  },
  RValuePerInch: {
    name: 'RValuePerInch',
    label: 'F·ft²·h/(Btu·in)',
    conversions: {
      RValuePerInchMetric: (1.0 / 5.678263) / 0.0254,
    },
    metric: 'RValuePerInchMetric',
    defaultPrecision: 3,
  },
  RValuePerInchMetric: {
    name: 'RValuePerInchMetric',
    label: 'K·m/W',
    defaultPrecision: 3,
  },
  UValue: {
    name: 'UValue',
    label: 'Btu/F·ft²·h',
    conversions: {
      UValueMetric: 5.678263,
      RValue: (val) => val > 0 ? 1.0 / val : 0,
    },
    metric: 'UValueMetric',
    defaultPrecision: 3,
  },
  UValueMetric: {
    name: 'UValueMetric',
    label: 'W/K·m²',
    conversions: {
      RValueMetric: (val) => val > 0 ? 1.0 / val : 0,
    },
    defaultPrecision: 2,
  },
  psi: {
    name: 'psi',
    label: 'psi',
    conversions: {
      kPA: 6.89476,
    },
    metric: 'kPA',
    defaultPrecision: 2,
  },
  PA: {
    name: 'PA',
    label: 'Pa',
    defaultPrecision: 2,
    conversions: {
      kPA: 1.0/1000.0,
    },
  },
  kPA: {
    name: 'kPA',
    label: 'kPa',
    defaultPrecision: 2,
  },
  in_wc: {
    name: 'in_wc',
    label: 'in. w. c.',
    conversions: {
      PA: 249.08891,
      psi: 1.0/27.71,
    },
    metric: 'PA',
    defaultPrecision: 2,
  },
  Insulation: {
    name: 'Insulation',
    label: 'F·ft²·h/Btu',
    conversions: {
      InsulationMetric: 5.678263,
    },
    metric: 'InsulationMetric',
    defaultPrecision: 2,
  },
  InsulationMetric: {
    name: 'InsulationMetric',
    label: 'K·m²/W',
    defaultPrecision: 2,
  },
  SoilConductivity: {
    name: 'Conductivity',
    label: 'Btu/h/ft/F',
    conversions: {
      SoilConductivityMetric: (1/3.415179) * (1.0/0.3048)*(9/5.0),
    },
    metric: 'SoilConductivityMetric',
    defaultPrecision: 2,
  },
  SoilConductivityMetric: {
    name: 'ConductivityMetric',
    label: 'W/m/K',
    defaultPrecision: 2,
  },
  Power: {
    name: 'Power',
    label: 'HP',
    conversions: {
      PowerMetric: 0.7457,
    },
    metric: 'PowerMetric',
    defaultPrecision: 2,
  },
  PowerMetric: {
    name: 'PowerMetric',
    label: 'kW',
    defaultPrecision: 2,
  },
  SpinSpeed: {
    name: 'SpinSpeed',
    label: 'rpm',
    defaultPrecision: 0,
  },
  HumidityRatio: {
    name: 'HumidityRatio',
    label: 'lb<sub>w</sub>/lb<sub>da</sub>',
    labelNoHtml: 'lbs wa / lb da',
    conversions: {
      HumidityRatioMetric: 1.0,
    },
    metric: 'HumidityRatioMetric',
    defaultPrecision: 4,
  },
  HumidityRatioMetric: {
    name: 'HumidityRatioMetric',
    label: 'kg<sub>w</sub>/kg<sub>da</sub>',
    labelNoHtml: 'kg wa / kg da',
    defaultPrecision: 4,
  },
  SpecificVolume: {
    name: 'SpecificVolume',
    label: 'ft³/lb<sub>da</sub>',
    conversions: {
      SpecificVolumeMetric: 0.3048*0.3048*0.3048/0.453592,
    },
    metric: 'SpecificVolumeMetric',
    defaultPrecision: 2,
  },
  SpecificVolumeMetric: {
    name: 'SpecificVolumeMetric',
    label: 'm³/kg<sub>da</sub>',
    defaultPrecision: 2,
  },
  Enthalpy: {
    name: 'Enthalpy',
    label: 'Btu/lb<sub>da</sub>',
    labelNoHtml: 'Btu/lb',
    // Imperial enthalpy is zero for dry air at 0F, while metric enthalpy is zero
    // for dry air at 0C (32F), so the conversion has an offset.
    conversions: {
      EnthalpyMetric: (val) => val * 2326.0 - ENTHALPY_ZERO_OFFSET_J_PER_KG,
    },
    metric: 'EnthalpyMetric',
    defaultPrecision: 2,
  },
  EnthalpyMetric: {
    name: 'EnthalpyMetric',
    label: 'J/kg<sub>da</sub>',
    labelNoHtml: 'J/kg',
    conversions: {
      Enthalpy: (val) => (val + ENTHALPY_ZERO_OFFSET_J_PER_KG) / 2326.0,
    },
    defaultPrecision: 2,
  },
  People: {
    name: 'People',
    label: 'People',
    defaultPrecision: 2,
  },
  // People per area
  PeoplePer1000ft2: {
    name: 'PeoplePerArea',
    label: 'People/1000ft²',
    conversions: {
      PeoplePer100m2: 1.0 / (10 * 0.3048 * 0.3048),
    },
    metric: 'PeoplePer100m2',
    defaultPrecision: 0,
  },
  PeoplePer100m2: {
    name: 'PeoplePerAreaMetric',
    label: 'People/100m²',
    defaultPrecision: 0,
  },
  // In the reference tables for occupancy density, they assume People/1000ft^2 is
  // the same as People/100m^2, so make special units to support this.
  PeoplePer1000ftOr100m2_Imperial: {
    name: 'PeoplePer1000ftOr100m2_Imperial',
    label: 'People/1000ft²',
    conversions: {
      PeoplePer1000ftOr100m2_Metric: 1.0,
    },
    metric: 'PeoplePer1000ftOr100m2_Metric',
    defaultPrecision: 0,
  },
  PeoplePer1000ftOr100m2_Metric: {
    name: 'PeoplePer1000ftOr100m2_Metric',
    label: 'People/100m²',
    defaultPrecision: 0,
  },
  // Air velocity — HVAC standard: feet per minute (imperial) / m/s (metric)
  // 1 fpm = 0.00508 m/s
  FtPerMin: {
    name: 'FtPerMin',
    label: 'fpm',
    conversions: {
      MPerSec: 0.00508,
    },
    metric: 'MPerSec',
    defaultPrecision: 0,
  },
  // Duct friction rate — in. w.c. per 100 ft of duct (imperial) / Pa/m (metric)
  // 1 in.wc/100ft = (249.089 Pa) / (30.48 m) = 8.1657 Pa/m
  InWcPer100Ft: {
    name: 'InWcPer100Ft',
    label: 'in. w.c./100 ft',
    conversions: {
      PaPer100M: 8.1657 * 30.48, // 1 in.wc/100ft = 8.1657 Pa/m × 30.48 m/100ft = 249.0 Pa/100m
    },
    metric: 'PaPer100M',
    defaultPrecision: 4,
  },
  PaPer100M: {
    name: 'PaPer100M',
    label: 'Pa/100 m',
    defaultPrecision: 2,
  },
});

function makeUnitConversionsMap(unitsEnum) {
  let conversions = {};
  for (const key of unitsEnum._keys) {
    conversions[key] = {
      // Self-conversion
      [key]: 1.0,
    };
  }
  for (const key of unitsEnum._keys) {
    let keyConversions = unitsEnum._data[key].conversions;
    if (keyConversions) {
      for (const dstKey in keyConversions) {
        if (typeof keyConversions[dstKey] == 'number') {
          conversions[key][dstKey] = keyConversions[dstKey];
          conversions[dstKey][key] = 1.0 / keyConversions[dstKey];
        } else {
          // Assume func
          conversions[key][dstKey] = keyConversions[dstKey];
        }
      }
    }
  }
  // We propagate the conversions info to the units nodes until there are no more
  // changes to be made (relaxation algo). We want the conversion info to be flat, for easy lookup.
  let madeChanges = true;
  while (madeChanges) {
    madeChanges = false;
    for (const key in conversions) {
      // n1 for neighbor-1, n2 for neighbor-2
      for (const n1Key in conversions[key]) {
        for (const n2Key in conversions[n1Key]) {
          if (!(n2Key in conversions[key]) && typeof conversions[key][n1Key] == 'number') {
            // console.log(`Adding conversion: ${key} => ${n2Key}`);
            conversions[key][n2Key] = conversions[key][n1Key] * conversions[n1Key][n2Key];
            madeChanges = true;
          }
        }
      }
    }
  }

  return conversions;
}
Units.conversionsMap = makeUnitConversionsMap(Units);
// console.log("Unit conversionsMap: ", Units.conversionsMap);

Units.convertValue = (val, valUnits, targetUnits) => {
  //console.log(`Converting value: ${val} ${valUnits} => ${targetUnits}`);
  if (targetUnits == valUnits) {
    return val;
  }
  if (targetUnits in Units.conversionsMap[valUnits]) {
    let conversion = Units.conversionsMap[valUnits][targetUnits];
    if (typeof conversion == 'number') {
      return val * conversion;
    } else if (typeof conversion == 'function') {
      return conversion(val);
    } else {
      throw new Error(`Invalid conversion type from ${valUnits} to ${targetUnits}: ${prettyJson(conversion)}`);
    }
  } else {
    console.log(`NO KNOWN CONVERSION FROM ${valUnits} TO ${targetUnits}: `, Units.conversionsMap);
    throw new Error(`No known conversion from ${valUnits} to ${targetUnits}`);
  }
}

Units.getDefaultPrecision = (units) => {
  if (!(units in Units)) {
    throw new Error(`Unknown units: ${units}`);
  }
  return valOr(Units._data[units].defaultPrecision, DEFAULT_PRECISION);
}

function applyValueIndent(str) {
  let parts = str.split('\n');
  if (parts.length == 1) {
    return str;
  }
  let res = parts[0] + '\n';
  for (let i = 1; i < parts.length; i++) {
    res += `  ${parts[i]}`;
    if (i < parts.length - 1) {
      res += '\n';
    }
  }
  return res;
}

function objectToValStr(obj, opts) {
  if ('toValString' in obj) {
    return obj.toValString();
  }
  let res = '{\n';
  let keys = Object.keys(obj);
  for (let i = 0; i < keys.length; i++) {
    let key = keys[i];
    let valueStr = applyValueIndent(`${valToStr(obj[key], opts)}`);
    res += `  ${key}: ${valueStr},\n`;
  }
  res += '}';
  return res;
}

export function valToStr(val, opts) {
  opts = valOr(opts, {})
  if (typeof val == 'number') {
    let numDecimals = valOr(opts.precision, 4)
    return formatNum(val, {maximumFractionDigits: numDecimals});
  } else if (typeof val == 'object' && val !== null) {
    return objectToValStr(val, opts);
  } else {
    return prettyJson(val);
  }
}

export function makeValStr(val, units, opts) {
  opts = valOr(opts, {})
  if (!(units in Units)) {
    throw new Error(`Unknown units: ${units}`);
  }
  let unitsStr = '';
  if (valOr(opts.includeUnits, true)) {
    unitsStr = Units._data[units].label;
    if (!valOr(opts.allowHtmlInLabels, true) && 'labelNoHtml' in Units._data[units]) {
      unitsStr = Units._data[units].labelNoHtml;
    }
    if (valOr(opts.includeSpace, false)) {
      unitsStr = ` ${unitsStr}`;
    }
  }
  let valToStrOpts = {
    precision: Units.getDefaultPrecision(units),
    ...opts,
  }
  return `${valToStr(val, valToStrOpts)}${unitsStr}`
}

export function roundToNumDecimals(value, numDecimals) {
  // NOTE - adding epsilon corrects some errors when converted values
  // like 2.49999999999
  const factor = Math.pow(10, numDecimals);
  return Math.round((value + Number.EPSILON) * factor) / factor;
} 

export function getLabel(units) {
  return Units._data[units].label;
}

Units.getLabel = getLabel;

export function getMetricUnits(units) {
  if (!(units in Units)) {
    return units;
  }
  let metricUnits = Units._data[units].metric;
  if (!metricUnits) {
    return units;
  }
  return metricUnits;
}

export function getColumnNameWithUnits(colName, units, opts) {
  opts = valOr(opts, {});
  if (!(units in Units)) {
    return colName;
  }
  let unitsLabel = getLabel(units);
  if (!valOr(opts.allowHtmlInLabels, true) && 'labelNoHtml' in Units._data[units]) {
    unitsLabel = Units._data[units].labelNoHtml;
  }
  if (unitsLabel == '') {
    return colName;
  }
  return `${colName} (${unitsLabel})`;
}

